import { h, ref, computed, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { cloneDeep } from 'lodash';
import { Message, Modal, Input, Textarea, Select, Button, Form, FormItem } from '@arco-design/web-vue';
import { initStore as useAppStore } from '@client/store/initStore';
import { memberDataStore as memberDataStoreRef } from '@client/store/memberDataStore';
import { userStore as useUserStore } from '@client/store/userStore';
import { strengthExercise } from '@client/api';
import { getStringByMaxBytes, getStringByte } from '@client/helper/string';
import { trainingProgramStore as useTrainingProgramStore } from '@client/store/trainingProgramStore';

const FIELDS_TO_TRANSFORM = ['part', 'muscle', 'muscleRelevance', 'equipment'];
const DEFAULT_STRENGTH_EXERCISE_DATA = {
  sportType: 4,
  exerciseType: 2,
  name: '',
  overview: '',
  // 身体部位
  part: '',
  // 肌肉群
  muscle: '',
  // 关联肌肉群
  muscleRelevance: '',
  // 器械类型
  equipment: ''
};

const CustomStrengthExerciseEditor = {
  props: {
    exerciseData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const trainingProgramStore = useTrainingProgramStore();
    const memberDataStore = memberDataStoreRef();
    const userStore = useUserStore();
    const formData = ref({});
    const saving = ref(false);
    const appStore = useAppStore();
    const { t } = useI18n();

    const parseOptionsFromExerciseCategory = (type) => {
      if (appStore.exerciseCategoryObj[type]) {
        return cloneDeep(
          Object.values(appStore.exerciseCategoryObj[type])
            .sort((a, b) => {
              return a.sortNo - b.sortNo;
            })
            .map((item) => {
              return {
                ...item,
                label: t(item.dataCode),
                value: item.dataValue
              };
            })
        );
      }

      return [];
    };

    const equipmentOptions = computed(() => parseOptionsFromExerciseCategory('equipment'));

    const selectedMuscleInfo = computed(() => {
      return (appStore.exerciseCategoryObj.muscle && appStore.exerciseCategoryObj.muscle[formData.value.muscle]) || {};
    });

    const selectedPartInfo = computed(() => {
      return (appStore.exerciseCategoryObj.part && appStore.exerciseCategoryObj.part[formData.value.part]) || {};
    });

    const options4part = computed(() => {
      const standardOptions = parseOptionsFromExerciseCategory('part');

      if (formData.value.muscle) {
        standardOptions.forEach((item) => {
          item.disabled = !selectedMuscleInfo.value.relevanceInPartQuery.split(',').includes(item.dataValue);
        });
      }

      return standardOptions;
    });

    const options4muscle = computed(() => {
      const standardOptions = parseOptionsFromExerciseCategory('muscle');

      if (formData.value.part) {
        standardOptions.forEach((item) => {
          item.disabled = !item.relevanceInPartQuery.split(',').includes(selectedPartInfo.value.dataValue);
        });
      }
      return standardOptions;
    });

    const isNewExercise = computed(() => !formData.value.id);

    const close = () => {
      emit('close');
    };
    const parseData2save = () => {
      const result = cloneDeep(formData.value);
      FIELDS_TO_TRANSFORM.forEach((field) => {
        result[field] = result[field] ? [result[field]] : [];
      });

      if (isNewExercise.value) {
        Object.assign(result, {
          access: 1,
          intensityCustom: 0,
          intensityMultiplier: 0,
          intensityType: 1,
          intensityValue: 0,
          intensityValueExtend: 0,
          restType: 1,
          restValue: 30,
          targetType: 3,
          targetValue: 15
        });
      }

      return result;
    };

    const handleSave = () => {
      if (!formData.value.name) {
        Message.error(t('R1009'));
        return;
      }

      if (!formData.value.part) {
        Message.error(t('R4019'));
        return;
      }

      if (![0, '0'].includes(formData.value.part) && !formData.value.muscle) {
        Message.error(t('R4022'));
        return;
      }

      saving.value = true;
      return strengthExercise[isNewExercise.value ? 'api4addStrengthExercise' : 'api4updateStrengthExercise']({
        data: parseData2save()
      })
        .then(() => {
          const { teamId, userId: teamUserId } = getUserParams();
          const userId = teamUserId || userStore.userId;
          trainingProgramStore.getExercises(teamId, userId);
          close();
        })
        .catch((err) => {
          console.error(err);
          Message.error(err.message);
        })
        .finally(() => {
          saving.value = false;
        });
    };
    const getUserParams = () => {
      const { teamViewMode, userData } = memberDataStore;

      if (teamViewMode && userData) {
        const { userId, teamId } = userData;
        return {
          userId,
          teamId
        };
      } else {
        return {};
      }
    };
    onBeforeMount(() => {
      const exerciseData2edit = cloneDeep(props.exerciseData);
      FIELDS_TO_TRANSFORM.forEach((field) => {
        exerciseData2edit[field] = String(exerciseData2edit?.[field]?.[0] ?? '');
      });
      formData.value = Object.assign({}, DEFAULT_STRENGTH_EXERCISE_DATA, exerciseData2edit);
    });

    return () => (
      <>
        <Form
          class="px-24 pt-10"
          layout="vertical"
        >
          <FormItem label={t('R4017')}>
            <Input
              class="border-border-1 bg-input-dark hover:bg-input-dark"
              v-model={formData.value.name}
              max-length={90}
              word-length={getStringByte}
              word-slice={getStringByMaxBytes}
              placeholder={t('R1009')}
            />
          </FormItem>
          <FormItem label={t('R1011')}>
            <Textarea
              class="border-border-1 bg-input-dark hover:bg-input-dark"
              v-model={formData.value.overview}
              placeholder={t('R1012')}
              max-length={200}
              show-word-limit
            />
          </FormItem>
          <FormItem
            class="form-item-select"
            v-slots={{
              label: () => (
                <div>
                  <span>{t('R4018')}</span>
                  <span class="color-gray-500">{t('R1013')}</span>
                </div>
              )
            }}
          >
            <Select
              class="hover:text-primary-6 border-border-1 hover:border-primary-500 bg-select-dark hover:bg-select-dark"
              allow-clear
              v-model={formData.value.part}
              placeholder={t('R4019')}
              options={options4part.value}
            />
          </FormItem>
          <FormItem
            class="form-item-select"
            v-slots={{
              label: () => (
                <div>
                  <span>{t('R4020')}</span>
                  <span class="color-gray-500">{t('R4021')}</span>
                </div>
              )
            }}
          >
            <Select
              class="hover:text-primary-6 border-border-1 hover:border-primary-500 bg-select-dark hover:bg-select-dark"
              allow-clear
              v-model={formData.value.muscle}
              placeholder={t('R4022')}
              options={options4muscle.value}
            />
          </FormItem>

          <FormItem
            class="form-item-select"
            v-slots={{
              label: () => (
                <div>
                  <span>{t('R4023')}</span>
                  <span class="color-gray-500">{t('R1014')}</span>
                </div>
              )
            }}
          >
            <Select
              class="hover:text-primary-6 border-border-1 hover:border-primary-500 bg-select-dark hover:bg-select-dark"
              allow-clear
              v-model={formData.value.equipment}
              placeholder={t('R4024')}
              options={equipmentOptions.value}
            />
          </FormItem>
        </Form>
        <div className="footer text-right">
          <Button
            class="mr-10"
            onClick={close}
          >
            {t('C1006')}
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving.value}
          >
            {t('G1006')}
          </Button>
        </div>
      </>
    );
  }
};

export const openModal4customStrengthExercise = (data, title) => {
  let openedModal;

  const closeModal = () => {
    openedModal?.close?.();
  };
  openedModal = Modal.open({
    title, // 占位
    content: () =>
      h(CustomStrengthExerciseEditor, {
        exerciseData: data,
        onClose: closeModal
      }),
    width: 600,
    titleAlign: 'start',
    closable: true,
    modalClass: 'custom-strength-exercise-editor coros',
    footer: false,
    unmountOnClose: true
  });

  return openedModal;
};
